import React, { type ReactNode, useCallback } from 'react';
import { useFragment, useMutation, graphql } from 'react-relay';
import type { SpotlightTourContainer$key as UserPreferencesKey } from '@atlassian/jira-relay/src/__generated__/SpotlightTourContainer.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Container } from '../../controllers/ai-tour/index.tsx';

type Props = {
	/**
	 * Child content to render.
	 */
	children: ReactNode;
	/**
	 * Relay fragment containing user preference data.
	 */
	userPreferences: UserPreferencesKey | null;
};

export const SpotlightTourContainer = ({ children, userPreferences }: Props) => {
	const cloudId = useCloudId();
	const data = useFragment<UserPreferencesKey>(
		graphql`
			fragment SpotlightTourContainer on JiraUserPreferences {
				isNaturalLanguageSpotlightTourEnabled
			}
		`,
		userPreferences,
	);

	const [commitMutation] = useMutation(graphql`
		mutation SpotlightTourContainerMutation($cloudId: ID!, $isEnabled: Boolean!) {
			jira {
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					setNaturalLanguageSpotlightTourEnabled(isEnabled: $isEnabled) {
						success
					}
				}
			}
		}
	`);

	// Disable the user preference when the tour starts so it's not shown on subsequent page loads
	const onDisableAITourUserPreference = useCallback(() => {
		commitMutation({
			variables: {
				cloudId,
				isEnabled: false,
			},
		});
	}, [cloudId, commitMutation]);

	return (
		<Container
			isAITourUserPreferenceEnabled={!!data?.isNaturalLanguageSpotlightTourEnabled}
			onDisableAITourUserPreference={onDisableAITourUserPreference}
		>
			{children}
		</Container>
	);
};
